import {
  channelToFrequencyMHz,
  frequenciesOverlapMhz,
  haversineDistanceM,
  DEFAULT_MIN_FREQ_GAP_MHZ,
} from './channelMapUtils';
import { deviceMapCoordinates, hasPublicWifi, isCapDevice, isNodeDevice } from './publicWifiRrmPlanUi';

/** Max distance (m) between survey samples treated as the same walk area. */
export const DEFAULT_SURVEY_PROXIMITY_M = 60;

function inferBandFromChannel(channel) {
  const ch = Number(channel);
  if (!Number.isFinite(ch) || ch <= 0) return '';
  if (ch >= 1 && ch <= 14) return '2.4GHz';
  if (ch >= 32 && ch <= 177) return '5GHz';
  if (ch >= 1 && ch <= 233) return '6GHz';
  return '';
}

export function sampleFrequencyMhz(sample) {
  const direct = Number(sample?.frequencyMhz ?? sample?.frequency_mhz);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const ch = Number(sample?.channel);
  if (!Number.isFinite(ch) || ch <= 0) return 0;
  const band = inferBandFromChannel(ch);
  return channelToFrequencyMHz(band, ch);
}

export function sampleBand(sample) {
  const freq = sampleFrequencyMhz(sample);
  if (freq >= 5900) return '6GHz';
  if (freq >= 4900) return '5GHz';
  if (freq >= 2400) return '2.4GHz';
  const ch = Number(sample?.channel);
  return inferBandFromChannel(ch) || 'unknown';
}

function gpsPointKey(lat, lng) {
  return `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
}

function isTargetSample(sample) {
  return Boolean(sample?.isTargetSsid || sample?.is_target_ssid);
}

function sampleCoords(sample) {
  const lat = Number(sample?.latitude);
  const lng = Number(sample?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

function sampleRssi(sample) {
  const rssi = Number(sample?.rssiDbm ?? sample?.rssi_dbm);
  return Number.isFinite(rssi) ? rssi : null;
}

function spectrumOverlap(freqA, widthA, freqB, widthB, minGapMHz) {
  const wA = Number(widthA) > 0 ? Number(widthA) : 20;
  const wB = Number(widthB) > 0 ? Number(widthB) : 20;
  const gap = Math.max(minGapMHz, Math.max(wA, wB) / 2);
  return frequenciesOverlapMhz(freqA, freqB, gap);
}

/** True when two scan rows occupy the same channel or overlapping spectrum. */
export function samplesShareChannelOrFrequency(target, foreign, minGapMHz = DEFAULT_MIN_FREQ_GAP_MHZ) {
  const tCh = Number(target?.channel);
  const fCh = Number(foreign?.channel);
  if (Number.isFinite(tCh) && Number.isFinite(fCh) && tCh > 0 && fCh > 0 && tCh === fCh) {
    return true;
  }
  const tFreq = sampleFrequencyMhz(target);
  const fFreq = sampleFrequencyMhz(foreign);
  if (!tFreq || !fFreq) return false;
  return spectrumOverlap(tFreq, 20, fFreq, 20, minGapMHz);
}

function summarizeSample(sample) {
  const channel = Number(sample?.channel);
  const frequencyMHz = sampleFrequencyMhz(sample);
  return {
    ssid: sample?.ssid || '',
    bssid: String(sample?.bssid || '').toLowerCase(),
    channel: Number.isFinite(channel) && channel > 0 ? channel : null,
    frequencyMHz: frequencyMHz ? Math.round(frequencyMHz) : null,
    band: sampleBand(sample),
    rssiDbm: sampleRssi(sample),
  };
}

function mergeClusterSamples(existing, incoming) {
  return {
    ...existing,
    samples: existing.samples.concat(incoming.samples),
    lat: (existing.lat * existing.weight + incoming.lat * incoming.weight) / (existing.weight + incoming.weight),
    lng: (existing.lng * existing.weight + incoming.lng * incoming.weight) / (existing.weight + incoming.weight),
    weight: existing.weight + incoming.weight,
    pointKeys: [...existing.pointKeys, ...incoming.pointKeys],
  };
}

/**
 * Group survey GPS fixes that fall within proximityM of each other.
 * ~100 unique fixes → cheap O(n²) merge; fine for typical surveys.
 */
export function buildSurveyLocationClusters(samples, proximityM = DEFAULT_SURVEY_PROXIMITY_M) {
  const byKey = new Map();
  (samples || []).forEach((sample) => {
    const coords = sampleCoords(sample);
    if (!coords) return;
    const key = gpsPointKey(coords.lat, coords.lng);
    const existing = byKey.get(key);
    if (existing) {
      existing.samples.push(sample);
      existing.weight += 1;
    } else {
      byKey.set(key, {
        id: key,
        lat: coords.lat,
        lng: coords.lng,
        weight: 1,
        pointKeys: [key],
        samples: [sample],
      });
    }
  });

  let clusters = [...byKey.values()];
  let merged = true;
  while (merged) {
    merged = false;
    const next = [];
    const used = new Set();
    for (let i = 0; i < clusters.length; i += 1) {
      if (used.has(i)) continue;
      let current = clusters[i];
      used.add(i);
      for (let j = i + 1; j < clusters.length; j += 1) {
        if (used.has(j)) continue;
        const other = clusters[j];
        const dist = haversineDistanceM(current.lat, current.lng, other.lat, other.lng);
        if (dist <= proximityM) {
          current = mergeClusterSamples(current, other);
          current.id = current.pointKeys.sort().join('|');
          used.add(j);
          merged = true;
        }
      }
      next.push(current);
    }
    clusters = next;
  }
  return clusters;
}

function overlapPairKey(clusterId, target, foreign) {
  return [
    clusterId,
    target.channel ?? target.frequencyMHz,
    target.bssid,
    foreign.channel ?? foreign.frequencyMHz,
    foreign.bssid,
  ].join('|');
}

/**
 * Co-channel interference inside the survey: a target (Juganu) reading and a
 * foreign AP reading within proximityM at overlapping channel/frequency.
 */
export function analyzeFieldSurveyChannelOverlaps(
  samples,
  {
    devices = [],
    proximityM = DEFAULT_SURVEY_PROXIMITY_M,
    minGapMHz = DEFAULT_MIN_FREQ_GAP_MHZ,
  } = {},
) {
  const clusters = buildSurveyLocationClusters(samples, proximityM);
  const overlapPairs = [];
  const overlapPairKeys = new Set();
  const overlapClusterIds = new Set();
  const overlapByPointKey = new Map();

  clusters.forEach((cluster) => {
    const targetSamples = cluster.samples.filter(isTargetSample);
    const foreignSamples = cluster.samples.filter((s) => !isTargetSample(s));
    if (!targetSamples.length || !foreignSamples.length) return;

    targetSamples.forEach((targetRaw) => {
      foreignSamples.forEach((foreignRaw) => {
        if (!samplesShareChannelOrFrequency(targetRaw, foreignRaw, minGapMHz)) return;
        const target = summarizeSample(targetRaw);
        const foreign = summarizeSample(foreignRaw);
        const pairKey = overlapPairKey(cluster.id, target, foreign);
        if (overlapPairKeys.has(pairKey)) return;
        overlapPairKeys.add(pairKey);
        overlapClusterIds.add(cluster.id);
        const pair = {
          clusterId: cluster.id,
          clusterLat: cluster.lat,
          clusterLng: cluster.lng,
          target,
          foreign,
        };
        overlapPairs.push(pair);
        cluster.pointKeys.forEach((pk) => {
          if (!overlapByPointKey.has(pk)) overlapByPointKey.set(pk, []);
          overlapByPointKey.get(pk).push(pair);
        });
      });
    });
  });

  overlapPairs.sort((a, b) => (b.foreign.rssiDbm ?? -999) - (a.foreign.rssiDbm ?? -999));

  const foreignAps = buildForeignApEntriesFromSamples(samples);
  const deviceConflictSerials = new Set();
  const deviceConflictDetails = new Map();

  if (overlapClusterIds.size > 0 && devices?.length) {
    devices.forEach((device) => {
      const serial = String(device?.serial_number || '').toLowerCase();
      if (!serial) return;
      const isCap = isCapDevice(device);
      const isPwNode = isNodeDevice(device) && hasPublicWifi(device);
      if (!isCap && !isPwNode) return;
      const coords = deviceMapCoordinates(device);
      if (!coords) return;

      const nearbyPairs = [];
      clusters.forEach((cluster) => {
        if (!overlapClusterIds.has(cluster.id)) return;
        const dist = haversineDistanceM(coords.lat, coords.lng, cluster.lat, cluster.lng);
        if (dist > proximityM) return;
        overlapPairs
          .filter((p) => p.clusterId === cluster.id)
          .forEach((p) => nearbyPairs.push({ ...p, distanceToDeviceM: dist }));
      });

      if (!nearbyPairs.length) return;
      deviceConflictSerials.add(serial);
      deviceConflictDetails.set(serial, {
        serial,
        deviceName: device.name || device.serial_number,
        nearbyOverlaps: nearbyPairs.slice(0, 12),
      });
    });
  }

  return {
    clusters,
    foreignAps,
    overlapPairs,
    overlapClusterIds,
    overlapByPointKey,
    overlapLocationCount: overlapClusterIds.size,
    deviceConflictSerials,
    deviceConflictDetails: [...deviceConflictDetails.values()],
    proximityM,
  };
}

/** Unique foreign (non-target) APs observed in the survey. */
export function buildForeignApEntriesFromSamples(samples) {
  const byKey = new Map();
  (samples || []).forEach((sample) => {
    if (isTargetSample(sample)) return;
    const channel = Number(sample?.channel);
    const frequencyMHz = sampleFrequencyMhz(sample);
    if ((!Number.isFinite(channel) || channel <= 0) && !frequencyMHz) return;
    const bssid = String(sample?.bssid || '').toLowerCase();
    const band = sampleBand(sample);
    const key = bssid
      ? `${bssid}|${channel || frequencyMHz}`
      : `${band}|${channel || frequencyMHz}|${String(sample?.ssid || '').toLowerCase()}`;
    const existing = byKey.get(key) || {
      key,
      band,
      channel: Number.isFinite(channel) ? channel : null,
      frequencyMHz: frequencyMHz ? Math.round(frequencyMHz) : null,
      ssid: sample?.ssid || '',
      bssid,
      sampleCount: 0,
      bestRssiDbm: null,
    };
    existing.sampleCount += 1;
    const rssi = sampleRssi(sample);
    if (rssi != null && (existing.bestRssiDbm == null || rssi > existing.bestRssiDbm)) {
      existing.bestRssiDbm = rssi;
    }
    if (!existing.ssid && sample?.ssid) existing.ssid = sample.ssid;
    byKey.set(key, existing);
  });
  return [...byKey.values()].sort(
    (a, b) => (b.sampleCount - a.sampleCount) || (b.bestRssiDbm ?? -999) - (a.bestRssiDbm ?? -999),
  );
}

/** Attach per walk-point overlap details from spatial survey analysis. */
export function buildLocationForeignOverlapIndex(locationSummaries, analysis) {
  const overlapByPointKey = analysis?.overlapByPointKey || new Map();
  return (locationSummaries || []).map((loc) => {
    const key = gpsPointKey(loc.latitude, loc.longitude);
    const overlapping = overlapByPointKey.get(key) || [];
    const foreignApCount = loc.other_sample_count ?? loc.otherSampleCount ?? 0;
    return {
      ...loc,
      foreignApCount,
      overlappingForeignCount: overlapping.length,
      overlappingForeign: overlapping.slice(0, 8),
    };
  });
}
